import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Trash2, Bookmark, ChevronDown, BookmarkPlus, Check, Pencil } from "lucide-react";
import { FilterPreset } from "@/lib/filterPresets";

interface FilterPresetBarProps<T> {
  presets: FilterPreset<T>[];
  currentFilters: T;
  hasActiveFilters: boolean;
  onApply: (filters: T) => void;
  onSave: (name: string, filters: T) => void;
  onRemove: (id: string) => void;
  onRename?: (id: string, name: string) => void;
}

export function FilterPresetBar<T>({
  presets,
  currentFilters,
  hasActiveFilters,
  onApply,
  onSave,
  onRemove,
  onRename,
}: FilterPresetBarProps<T>) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [savedId, setSavedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingId) setTimeout(() => editInputRef.current?.focus(), 30);
  }, [editingId]);

  useEffect(() => {
    if (saving && open) inputRef.current?.focus();
  }, [saving, open]);

  function handleSave() {
    if (!saveName.trim()) return;
    onSave(saveName.trim(), currentFilters);
    setSaveName("");
    setSaving(false);
    // Flash a "saved" confirmation
    const newId = saveName.trim();
    setSavedId(newId);
    setTimeout(() => setSavedId(null), 2000);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") { setSaving(false); setSaveName(""); }
  }

  function handleApply(preset: FilterPreset<T>) {
    onApply(preset.filters);
    setOpen(false);
  }

  function startEdit(preset: FilterPreset<T>, e: React.MouseEvent) {
    e.stopPropagation();
    setEditingId(preset.id);
    setEditingName(preset.name);
  }

  function commitEdit() {
    if (editingId && editingName.trim()) {
      onRename?.(editingId, editingName.trim());
    }
    setEditingId(null);
    setEditingName("");
  }

  function cancelEdit() {
    setEditingId(null);
    setEditingName("");
  }

  function handleEditKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
    if (e.key === "Escape") cancelEdit();
    e.stopPropagation();
  }

  const hasPresets = presets.length > 0;

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setSaving(false); setSaveName(""); } }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-1.5 text-sm font-normal"
        >
          <Bookmark className="h-3.5 w-3.5" />
          Saved Filters
          {hasPresets && (
            <span className="ml-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold w-4 h-4 flex items-center justify-center">
              {presets.length}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 ml-0.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-64 p-0">
        {/* Saved presets list */}
        {hasPresets ? (
          <div className="py-1">
            <p className="text-xs text-muted-foreground px-3 pt-2 pb-1 font-medium uppercase tracking-wide">
              Your saved filters
            </p>
            {presets.map((preset) => (
              <div
                key={preset.id}
                className="flex items-center justify-between px-3 py-2 hover:bg-accent group cursor-pointer"
                onClick={() => editingId === preset.id ? undefined : handleApply(preset)}
              >
                {editingId === preset.id ? (
                  <div className="flex items-center gap-1.5 flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
                    <Input
                      ref={editInputRef}
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={handleEditKeyDown}
                      onBlur={commitEdit}
                      className="h-7 text-xs flex-1"
                    />
                    <button className="text-green-600 hover:text-green-700 shrink-0" onClick={commitEdit} title="Save name">
                      <Check className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 min-w-0">
                      <Bookmark className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-sm truncate">{preset.name}</span>
                    </div>
                    <div className="flex items-center gap-1 ml-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      {onRename && (
                        <button
                          className="text-muted-foreground hover:text-foreground"
                          onClick={(e) => startEdit(preset, e)}
                          title="Rename"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      )}
                      <button
                        className="text-muted-foreground hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); onRemove(preset.id); }}
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="px-3 py-4 text-center">
            <Bookmark className="h-6 w-6 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No Saved Filters Yet</p>
            <p className="text-xs text-muted-foreground/70 mt-0.5">
              {hasActiveFilters ? "Save your current filters below." : "Set some filters first, then save them here."}
            </p>
          </div>
        )}

        {/* Save section */}
        {hasActiveFilters && (
          <>
            {hasPresets && <Separator />}
            <div className="p-3">
              {!saving ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-2 justify-start text-sm"
                  onClick={() => { setSaving(true); setTimeout(() => inputRef.current?.focus(), 50); }}
                >
                  <BookmarkPlus className="h-3.5 w-3.5" />
                  Save Current Filters…
                </Button>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground font-medium">Name this filter set</p>
                  <Input
                    ref={inputRef}
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="e.g. My 1040s 2024"
                    className="h-8 text-sm"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1 h-8 text-xs gap-1"
                      onClick={handleSave}
                      disabled={!saveName.trim()}
                    >
                      <Check className="h-3 w-3" />
                      Save
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => { setSaving(false); setSaveName(""); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
